/**
 * MODULE: _shared/frontend-shell/src/features/crm/data/mockCrmData.dev.ts
 * PURPOSE: CRM demo KPIs plus seed arrays for Playwright, Vitest, and local-only fallbacks
 * DEPENDENCIES: ../types/*
 * PUBLIC_API: MOCK_CRM_KPIS, MOCK_OPPORTUNITIES, MOCK_TASKS, MOCK_ACTIVITIES, MOCK_CRM_OPPORTUNITIES, MOCK_CRM_TASKS, MOCK_CRM_ACTIVITIES
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 453/500
 * RECENT: 2026-05-11 Task 8 #91 — **`pipeline`**, **`territory`**, **`tasks`** saved-view dev seeds
 * RECENT_PRIOR: 2026-05-08 [2.2.884] **`DEV_SEED`** **`tasks`** list for **`/crm/tasks`** hub — root **`CHANGELOG` [2.2.884]`**
 * RECENT_PRIOR: 2026-04-30 [2.2.758] **`DEV_SEED_CRM_SAVED_VIEWS`** dev-only CRM named filter demos — CHANGELOG [2.2.758]. [2.2.740] **`opp-01`** **`primaryContactId`** **`c1`** — CHANGELOG [2.2.740]
 */

import type { Activity } from '../types/activity.types';
import type { CrmKpis } from '../types/crm.types';
import type { Opportunity } from '../types/opportunity.types';
import type { Task } from '../types/task.types';

const iso = (d: string): string => `${d}T12:00:00.000Z`;

/** CRM saved-views entity keys used by `useCrmSavedViews` dev merge (ids prefixed so DELETE skips API). */
export type CrmSavedViewsEntityDev = 'deals' | 'contacts' | 'territory' | 'tasks' | 'pipeline';

export type DevSeedCrmSavedView = {
  id: string;
  name: string;
  entity: CrmSavedViewsEntityDev;
  filters: Record<string, string>;
};

/** Pre-seeded named filters for local Vitest or dev shell (merged before server rows). */
export const DEV_SEED_CRM_SAVED_VIEWS: Record<CrmSavedViewsEntityDev, DevSeedCrmSavedView[]> = {
  deals: [
    {
      id: 'dev-seed-deals-open',
      name: 'My open deals',
      entity: 'deals',
      filters: { stage: 'active', owner: 'user-1' },
    },
    {
      id: 'dev-seed-deals-month',
      name: 'Closing this month',
      entity: 'deals',
      filters: { stage: 'negotiate_close', territory: 'terr-east' },
    },
    {
      id: 'dev-seed-deals-risk',
      name: 'At-risk pipeline',
      entity: 'deals',
      filters: { stage: 'qualified', territory: 'terr-west' },
    },
  ],
  contacts: [
    {
      id: 'dev-seed-contacts-unassigned',
      name: 'Unassigned contacts',
      entity: 'contacts',
      filters: { tag: 'inactive', territory: 'terr-central' },
    },
    {
      id: 'dev-seed-contacts-recent',
      name: 'Recent additions',
      entity: 'contacts',
      filters: { tag: 'active', owner: 'user-1' },
    },
  ],
  territory: [
    {
      id: 'dev-seed-territory-west',
      name: 'West region focus',
      entity: 'territory',
      filters: { region: 'west' },
    },
    {
      id: 'dev-seed-territory-search',
      name: 'Enterprise keyword',
      entity: 'territory',
      filters: { q: 'enterprise' },
    },
  ],
  tasks: [
    {
      id: 'dev-seed-tasks-overdue',
      name: 'Overdue tasks',
      entity: 'tasks',
      filters: { due: 'overdue', status: 'todo' },
    },
    {
      id: 'dev-seed-tasks-my-open',
      name: 'My open tasks',
      entity: 'tasks',
      filters: { status: 'todo', due: 'this_week' },
    },
  ],
  pipeline: [
    {
      id: 'dev-seed-pipeline-open',
      name: 'Open pipeline',
      entity: 'pipeline',
      filters: { status: 'open' },
    },
    {
      id: 'dev-seed-pipeline-my-prospects',
      name: 'My prospects',
      entity: 'pipeline',
      filters: { owner: 'user-1', status: 'open' },
    },
    {
      id: 'dev-seed-pipeline-active-sequences',
      name: 'Active sequences',
      entity: 'pipeline',
      filters: { status: 'scheduled' },
    },
  ],
};

/** Instruction 3 — headline KPIs for dev or test */
export const MOCK_CRM_KPIS: CrmKpis = {
  totalPipeline: 1_840_000,
  accountCount: 142,
  activeDeals: 34,
  weightedPipeline: 890_000,
};

/** Instruction 3 — eight pipeline rows with mixed stages */
export const MOCK_OPPORTUNITIES: Opportunity[] = [
  {
    id: 'opp-01',
    name: 'Northwind — Platform expansion',
    accountId: 'acct-1',
    accountName: 'Northwind Traders',
    primaryContactId: 'c1',
    primaryContactName: 'Sarah Jones',
    ownerId: 'user-1',
    ownerName: 'Morgan Lee',
    stage: 'prospecting',
    amount: 180_000,
    weightedAmount: 36_000,
    closeDateISO: iso('2026-06-15'),
    probability: 20,
    source: 'inbound',
    territoryId: 'terr-west',
    territoryName: 'West',
    nextStep: 'Qualify budget owner',
    lastActivityDateISO: iso('2026-04-26'),
    tags: ['New'],
    createdAtISO: iso('2026-04-10'),
    updatedAtISO: iso('2026-04-28'),
  },
  {
    id: 'opp-02',
    name: 'Contoso — Business intelligence suite',
    accountId: 'acct-2',
    accountName: 'Contoso Ltd',
    ownerId: 'user-2',
    ownerName: 'Alex Kim',
    stage: 'qualified',
    amount: 420_000,
    weightedAmount: 126_000,
    closeDateISO: iso('2026-07-01'),
    probability: 30,
    source: 'unknown',
    territoryId: 'terr-mw',
    territoryName: 'Midwest',
    nextStep: 'Pilot workshop',
    createdAtISO: iso('2026-04-05'),
    updatedAtISO: iso('2026-04-27'),
  },
  {
    id: 'opp-03',
    name: 'Fabrikam — Renewal uplift',
    accountId: 'acct-3',
    accountName: 'Fabrikam Inc',
    primaryContactId: 'con-3',
    primaryContactName: 'Riley Ortiz',
    ownerId: 'user-1',
    ownerName: 'Morgan Lee',
    stage: 'use_cases',
    amount: 95_000,
    weightedAmount: 47_500,
    closeDateISO: iso('2026-05-20'),
    probability: 50,
    source: 'marketing',
    territoryId: 'terr-west',
    territoryName: 'West',
    createdAtISO: iso('2026-03-01'),
    updatedAtISO: iso('2026-04-28'),
  },
  {
    id: 'opp-04',
    name: 'Adventure Works — CX rollout',
    accountId: 'acct-4',
    accountName: 'Adventure Works',
    ownerId: 'user-3',
    ownerName: 'Jordan Patel',
    stage: 'negotiate_close',
    amount: 890_000,
    weightedAmount: 623_000,
    closeDateISO: iso('2026-05-30'),
    probability: 70,
    source: 'partner',
    territoryId: 'terr-east',
    territoryName: 'East',
    nextStep: 'MSA countersign',
    tags: ['Strategic'],
    createdAtISO: iso('2026-02-12'),
    updatedAtISO: iso('2026-04-28'),
  },
  {
    id: 'opp-05',
    name: 'Litware — Data residency',
    accountId: 'acct-5',
    accountName: 'Litware',
    ownerId: 'user-2',
    ownerName: 'Alex Kim',
    stage: 'qualified',
    amount: 310_000,
    weightedAmount: 93_000,
    closeDateISO: iso('2026-08-10'),
    probability: 30,
    source: 'outbound',
    createdAtISO: iso('2026-04-18'),
    updatedAtISO: iso('2026-04-26'),
  },
  {
    id: 'opp-06',
    name: 'Wide World Importers — SDR tooling',
    accountId: 'acct-6',
    accountName: 'Wide World Importers',
    ownerId: 'user-4',
    ownerName: 'Sam Rivera',
    stage: 'prospecting',
    amount: 72_000,
    weightedAmount: 14_400,
    closeDateISO: iso('2026-06-30'),
    probability: 20,
    source: 'inbound',
    territoryId: 'terr-mw',
    territoryName: 'Midwest',
    createdAtISO: iso('2026-04-22'),
    updatedAtISO: iso('2026-04-27'),
  },
  {
    id: 'opp-07',
    name: 'Blue Yonder — Competitive takeout',
    accountId: 'acct-7',
    accountName: 'Blue Yonder Airlines',
    ownerId: 'user-1',
    ownerName: 'Morgan Lee',
    stage: 'closed_won',
    amount: 410_000,
    weightedAmount: 410_000,
    closeDateISO: iso('2026-04-10'),
    probability: 100,
    source: 'referral',
    createdAtISO: iso('2026-01-08'),
    updatedAtISO: iso('2026-04-10'),
  },
  {
    id: 'opp-08',
    name: 'Tailspin Toys — Compliance package',
    accountId: 'acct-8',
    accountName: 'Tailspin Toys',
    ownerId: 'user-3',
    ownerName: 'Jordan Patel',
    stage: 'closed_lost',
    amount: 55_000,
    weightedAmount: 0,
    closeDateISO: iso('2026-03-28'),
    probability: 0,
    source: 'unknown',
    notes: 'Paused hiring — no budget.',
    createdAtISO: iso('2026-02-01'),
    updatedAtISO: iso('2026-03-28'),
  },
];

/** Instruction 3 — six tasks with varied status and TaskType */
export const MOCK_TASKS: Task[] = [
  {
    id: 'task-1',
    title: 'Follow up — renewal pricing',
    description: 'Send revised quote to procurement.',
    type: 'follow_up',
    status: 'todo',
    priority: 'high',
    ownerId: 'user-1',
    ownerName: 'Morgan Lee',
    dueDateISO: iso('2026-05-02'),
    relatedEntityId: 'opp-03',
    relatedEntityType: 'deal',
    relatedEntityName: 'Fabrikam — Renewal uplift',
    subSteps: { completed: 2, total: 5 },
    createdAtISO: iso('2026-04-20'),
    updatedAtISO: iso('2026-04-28'),
  },
  {
    id: 'task-2',
    title: 'Discovery call prep',
    type: 'task',
    status: 'in_progress',
    priority: 'medium',
    ownerId: 'user-2',
    ownerName: 'Alex Kim',
    dueDateISO: iso('2026-04-30'),
    relatedEntityId: 'acct-7',
    relatedEntityType: 'account',
    relatedEntityName: 'Blue Yonder Airlines',
    createdAtISO: iso('2026-04-22'),
    updatedAtISO: iso('2026-04-27'),
  },
  {
    id: 'task-3',
    title: 'Territory plan Q2 review',
    type: 'task',
    status: 'done',
    priority: 'low',
    ownerId: 'user-1',
    ownerName: 'Morgan Lee',
    dueDateISO: iso('2026-04-25'),
    relatedEntityId: 'terr-west',
    relatedEntityType: 'territory',
    relatedEntityName: 'West',
    createdAtISO: iso('2026-04-10'),
    updatedAtISO: iso('2026-04-26'),
  },
  {
    id: 'task-4',
    title: 'Send security questionnaire',
    type: 'task',
    status: 'overdue',
    priority: 'urgent',
    ownerId: 'user-3',
    ownerName: 'Jordan Patel',
    dueDateISO: iso('2026-04-18'),
    relatedEntityId: 'opp-08',
    relatedEntityType: 'deal',
    relatedEntityName: 'Tailspin Toys — Compliance package',
    createdAtISO: iso('2026-04-12'),
    updatedAtISO: iso('2026-04-19'),
  },
  {
    id: 'task-5',
    title: 'Schedule exec alignment',
    type: 'meeting',
    status: 'todo',
    priority: 'urgent',
    ownerId: 'user-2',
    ownerName: 'Alex Kim',
    dueDateISO: iso('2026-05-05'),
    relatedEntityId: 'opp-05',
    relatedEntityType: 'deal',
    relatedEntityName: 'Litware — Data residency',
    createdAtISO: iso('2026-04-26'),
    updatedAtISO: iso('2026-04-28'),
  },
  {
    id: 'task-6',
    title: 'Update contact roles',
    type: 'task',
    status: 'in_progress',
    priority: 'low',
    ownerId: 'user-4',
    ownerName: 'Sam Rivera',
    dueDateISO: iso('2026-05-08'),
    relatedEntityId: 'con-1',
    relatedEntityType: 'contact',
    relatedEntityName: 'Jamie Chen',
    createdAtISO: iso('2026-04-21'),
    updatedAtISO: iso('2026-04-27'),
  },
];

/** Instruction 3 — five activities */
export const MOCK_ACTIVITIES: Activity[] = [
  {
    id: 'act-1',
    type: 'call',
    subject: 'Outbound — pilot scope',
    relatedEntityId: 'opp-02',
    relatedEntityType: 'deal',
    relatedEntityName: 'Contoso — Business intelligence suite',
    ownerId: 'user-1',
    ownerName: 'Morgan Lee',
    dueAtISO: iso('2026-05-01'),
    status: 'scheduled',
    notes: 'Confirm stakeholders.',
    createdAtISO: iso('2026-04-26'),
    updatedAtISO: iso('2026-04-28'),
  },
  {
    id: 'act-2',
    type: 'email',
    subject: 'Send proposal v2',
    relatedEntityId: 'opp-04',
    relatedEntityType: 'deal',
    relatedEntityName: 'Adventure Works — CX rollout',
    ownerId: 'user-2',
    ownerName: 'Alex Kim',
    dueAtISO: iso('2026-04-29'),
    status: 'open',
    createdAtISO: iso('2026-04-27'),
    updatedAtISO: iso('2026-04-28'),
  },
  {
    id: 'act-3',
    type: 'meeting',
    subject: 'Executive business review',
    relatedEntityId: 'acct-2',
    relatedEntityType: 'account',
    relatedEntityName: 'Contoso Ltd',
    ownerId: 'user-1',
    ownerName: 'Morgan Lee',
    dueAtISO: iso('2026-05-06'),
    status: 'scheduled',
    createdAtISO: iso('2026-04-20'),
    updatedAtISO: iso('2026-04-28'),
  },
  {
    id: 'act-4',
    type: 'note',
    subject: 'Champion confirmed budget band',
    relatedEntityId: 'opp-05',
    relatedEntityType: 'deal',
    relatedEntityName: 'Litware — Data residency',
    ownerId: 'user-3',
    ownerName: 'Jordan Patel',
    dueAtISO: iso('2026-04-28'),
    status: 'complete',
    outcome: 'Budget £250k–£400k',
    createdAtISO: iso('2026-04-25'),
    updatedAtISO: iso('2026-04-28'),
  },
  {
    id: 'act-5',
    type: 'call',
    subject: 'Check-in — adoption metrics',
    relatedEntityId: 'opp-01',
    relatedEntityType: 'deal',
    relatedEntityName: 'Northwind — Platform expansion',
    ownerId: 'user-2',
    ownerName: 'Alex Kim',
    dueAtISO: iso('2026-04-22'),
    status: 'overdue',
    createdAtISO: iso('2026-04-18'),
    updatedAtISO: iso('2026-04-22'),
  },
];

/** Legacy barrel names — same references as Instruction 3 exports */
export const MOCK_CRM_OPPORTUNITIES = MOCK_OPPORTUNITIES;
export const MOCK_CRM_TASKS = MOCK_TASKS;
export const MOCK_CRM_ACTIVITIES = MOCK_ACTIVITIES;
